import React, { useEffect, useState } from 'react'
import './totalIncentive.css'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { Button, Fade, Menu } from '@mui/material'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import { utils, writeFile } from 'xlsx-js-style'
import { detailsField } from '../../../services/employeeFieldType'
import { salarySlipYear } from '../../../util_service/generateYear/generateYear'
import { currencyCode } from '../../../services/constant'
import CurrencyCellRenderer from '../../currency-cell-render/currency-cell-render'
import AgGridContainer from '../../../componant/gridContainer/gridContainer'
import { SnackbarService } from '../../../services/snackBehaviorSubject/snackBehaviorSubject'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { getTotalIncentiveData } from '../../../api-services/employee-services'

interface SelectedYearMonth {
  month: string
  year: string
  monthName: string
}

interface TotalIncentiveRow {
  employeeId: string
  employeeName: string
  totalCount: number
  totalIncentive: number
}

const TotalIncentive = () => {
  const snackbarService = SnackbarService()
  const detailsYearField = detailsField()
  const currency = currencyCode()

  const [selectedYearMonth, setSelectedYearMonth] = useState<SelectedYearMonth>({
    month: '',
    year: '',
    monthName: ''
  })
  const [rowData, setRowData] = useState<TotalIncentiveRow[]>([])

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const openFilterModal = Boolean(anchorEl)
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  const [showFilter, setFilter] = useState(false)

  const getMonthName = (monthNumber: any) => {
    const date = new Date()
    date.setMonth(monthNumber - 1)
    return date.toLocaleString('en-US', { month: 'long' })
  }

  useEffect(() => {
    const currentDate = new Date()
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth() + 1
    setSelectedYearMonth({ month: `${month}`, year: `${year}`, monthName: getMonthName(month) })
  }, [])

  const fetchTotalIncentiveData = () => {
    getTotalIncentiveData(Number(selectedYearMonth.month), Number(selectedYearMonth.year)).then((response: any) => {
      console.log('Total Incentive Data:', response)
      if (response.status === 200) {
        setRowData(response.data)
      } else {
        snackbarService.next({ data: { open: true, severity: 'error', message: 'Failed to fetch total incentive data' } })
      }
    }).catch((error) => {
      snackbarService.next({ data: { open: true, severity: 'error', message: error.message } })
    })
  }

  useEffect(() => {
    if (selectedYearMonth.month !== '' && selectedYearMonth.year !== '') {
      fetchTotalIncentiveData()
      handleCloseFilter()
    }
  }, [selectedYearMonth])

  const onMenuChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target
    setSelectedYearMonth((prev) => {
      return { ...prev, [name]: value.toString(), monthName: getMonthName(name === 'month' ? value : prev.month) }
    })
  }

  const handleOpenFilter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseFilter = () => {
    setAnchorEl(null)
  }

  const handleScreenSize = () => {
    const width = window.innerWidth
    if (width !== screenWidth) {
      setScreenWidth(width)
    }
  }

  useEffect(() => {
    window.addEventListener('resize', handleScreenSize)
  }, [])

  useEffect(() => {
    if (screenWidth < 460 || (screenWidth >= 600 && screenWidth <= 773)) {
      setFilter(true)
    } else {
      setFilter(false)
      setAnchorEl(null)
    }
    window.addEventListener('resize', handleScreenSize)
  }, [screenWidth])

  const columnDefs = [
    {
      headerName: 'Employee Id',
      field: 'employeeId',
      minWidth: 150,
      flex: 1,
      resizable: true,
      filter: true
    },
    {
      headerName: 'Employee Name',
      field: 'employeeName',
      minWidth: 200,
      flex: 1,
      resizable: true,
      filter: true
    },
    {
      headerName: 'Total Count',
      field: 'totalCount',
      minWidth: 150,
      flex: 1,
      resizable: true,
      filter: true
    },
    {
      headerName: 'Total Incentive Amount',
      field: 'totalIncentive',
      minWidth: 200,
      flex: 1,
      resizable: true,
      filter: true,
      cellRenderer: (params: any) => (
        <CurrencyCellRenderer value={params.value} currencyCode={currency} />
      )
    }
  ]

  const handleDownloadExcel = () => {
    if (rowData.length === 0) {
      snackbarService.next({ data: { open: true, severity: 'error', message: 'Data not available' } })
      return
    }
    const header = ['Employee Id', 'Employee Name', 'Total Count', 'Total Incentive Amount']
    const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(value)
    const data = rowData.map((row) => ([
      row.employeeId,
      row.employeeName,
      row.totalCount,
      formatCurrency(row.totalIncentive)
    ]))
    const ws = utils.aoa_to_sheet([header, ...data])

    header.forEach((_, colIndex) => {
      const cellRef = utils.encode_cell({ r: 0, c: colIndex })
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true }
        }
      }
    })

    ws['!cols'] = header.map((headerText, colIndex) => {
      const maxDataLength = data.reduce((max, row) => {
        const value = row[colIndex]
        const length = value !== undefined && value !== null ? String(value).length : 0
        return Math.max(max, length)
      }, headerText.length)
      return { wch: maxDataLength + 2 }
    })

    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, 'Total Incentive')
    writeFile(wb, `Total Incentive_${selectedYearMonth.monthName}_${selectedYearMonth.year}.xlsx`)
  }

  return (
    <div className='main'>
      <div className='main-container'>
        <div className='heading heading-center-space-between height-40'>
          <h2 className='single-line-text'>Total Incentive</h2>
          <div className='salary-slip-search-container'>
            {!showFilter ? <div className='year-month-picker'>
              <FormControl fullWidth>
                <InputLabel id="total-incentive-year-label">Year</InputLabel>
                <Select
                  name='year'
                  labelId="total-incentive-year-label"
                  id="total-incentive-year"
                  label="Year"
                  value={selectedYearMonth.year}
                  onChange={onMenuChange}
                  sx={{ height: '30px' }}
                >
                  {
                    salarySlipYear.map((item: any, index: number) => {
                      return <MenuItem value={item.value} key={index}>{item.name}</MenuItem>
                    })
                  }
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="total-incentive-month-label">Month</InputLabel>
                <Select
                  name='month'
                  labelId="total-incentive-month-label"
                  id="total-incentive-month"
                  label="Month"
                  value={selectedYearMonth.month}
                  onChange={onMenuChange}
                  sx={{ height: '30px' }}
                >
                  {
                    detailsYearField.salarySlipMonth.map((item: any, index: number) => {
                      return <MenuItem value={item.value} key={index}>{item.name}</MenuItem>
                    })
                  }
                </Select>
              </FormControl>
            </div> :
              <div className='filter-date-picker'
                onClick={handleOpenFilter}
                aria-controls={openFilterModal ? 'fade-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={openFilterModal ? 'true' : undefined}
              >
                <FilterAltOutlinedIcon style={{ color: 'var(--primaryColor)' }} />
                <label style={{ fontSize: '10px', marginTop: '9px', color: 'var(--primaryColor)', fontWeight: 'bold' }}>Filter</label>
              </div>
            }

            <Menu
              id="fade-menu"
              MenuListProps={{
                'aria-labelledby': 'fade-button',
              }}
              anchorEl={anchorEl}
              open={openFilterModal}
              onClose={handleCloseFilter}
              TransitionComponent={Fade}
            >
              <MenuItem className='date-pickers'>
                <FormControl fullWidth>
                  <InputLabel id="total-incentive-year-label-mobile">Year</InputLabel>
                  <Select
                    name='year'
                    labelId="total-incentive-year-label-mobile"
                    id="total-incentive-year-mobile"
                    label="Year"
                    value={selectedYearMonth.year}
                    onChange={onMenuChange}
                    sx={{ height: '30px' }}
                  >
                    {
                      salarySlipYear.map((item: any, index: number) => {
                        return <MenuItem value={item.value} key={index}>{item.name}</MenuItem>
                      })
                    }
                  </Select>
                </FormControl>
              </MenuItem>
              <MenuItem className='date-pickers'>
                <FormControl fullWidth>
                  <InputLabel id="total-incentive-month-label-mobile">Month</InputLabel>
                  <Select
                    name='month'
                    labelId="total-incentive-month-label-mobile"
                    id="total-incentive-month-mobile"
                    label="Month"
                    value={selectedYearMonth.month}
                    onChange={onMenuChange}
                    sx={{ height: '30px' }}
                  >
                    {
                      detailsYearField.salarySlipMonth.map((item: any, index: number) => {
                        return <MenuItem value={item.value} key={index}>{item.name}</MenuItem>
                      })
                    }
                  </Select>
                </FormControl>
              </MenuItem>
            </Menu>
          </div>
        </div>
        <div className='cal-40'>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <h4 className='total-incentive-subheading'>Total Incentive of {selectedYearMonth.monthName} {selectedYearMonth.year}</h4>
            <div className='total-incentive-table-header'>
              <Button
                variant='outlined'
                className='total-incentive-download-button'
                startIcon={<DownloadIcon />}
                onClick={handleDownloadExcel}
              >
                Download Excel
              </Button>
            </div>
            <div className='ag-grid-container' style={{ flex: 1 }}>
              <AgGridContainer rowData={rowData} columnDefs={columnDefs} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TotalIncentive
